import { siteTexts } from "../../../../shared/i18n/siteTexts";
import { parseDateTimeValue } from "../../../../shared/utils/dateTime";
import {
  OpenMatchGenderPreference,
  OpenMatchItem,
  OpenMatchPaymentMode,
  OpenMatchSidePreference,
} from "../types/matches";

function pad(value: number) {
  return String(value).padStart(2, "0");
}

export function parseOpenMatchDateTimeValue(value?: string | null) {
  const raw = String(value || "").trim();
  const match = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T|\s)(\d{2}):(\d{2})(?::(\d{2}))?/,
  );
  if (match) {
    const [, year, month, day, hour, minute, second = "00"] = match;
    const parsed = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    );
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }
  return parseDateTimeValue(raw);
}

function formatOpenMatchDateLabel(value?: string | null) {
  const parsed = parseOpenMatchDateTimeValue(value);
  if (!parsed) return "-";
  return `${pad(parsed.getDate())}/${pad(parsed.getMonth() + 1)}/${parsed.getFullYear()}`;
}

function formatOpenMatchTimeLabel(value?: string | null) {
  const parsed = parseOpenMatchDateTimeValue(value);
  if (!parsed) return "-";
  return `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
}

export function resolveOpenMatchDurationMinutes(match: Pick<OpenMatchItem, "startAt" | "endAt">) {
  const startAt = parseOpenMatchDateTimeValue(match.startAt);
  const endAt = parseOpenMatchDateTimeValue(match.endAt);
  if (!startAt || !endAt) return 0;
  if (Number.isNaN(startAt.getTime()) || Number.isNaN(endAt.getTime())) return 0;
  return Math.max(0, Math.round((endAt.getTime() - startAt.getTime()) / 60000));
}

export function isOpenMatchFinished(
  match: Pick<OpenMatchItem, "endAt">,
  now = Date.now(),
) {
  const endAt = parseOpenMatchDateTimeValue(match.endAt);
  if (!endAt || Number.isNaN(endAt.getTime())) return false;
  return endAt.getTime() <= now;
}

export function resolveOpenMatchFilledSlots(
  match: Pick<OpenMatchItem, "organizerPlays" | "participantSummary">,
) {
  return (
    (match.organizerPlays ? 1 : 0) +
    Number(match.participantSummary?.confirmed || 0) +
    Number(match.participantSummary?.invited || 0) +
    Number(match.participantSummary?.pending || 0)
  );
}

export function resolveOpenMatchAvailableSlots(
  match: Pick<
    OpenMatchItem,
    "organizerPlays" | "participantSummary" | "totalPlayers" | "confirmedAt"
  >,
) {
  if (match.confirmedAt) return 0;
  return Math.max(0, Number(match.totalPlayers || 0) - resolveOpenMatchFilledSlots(match));
}

export function resolveOpenMatchLowestPerPlayerCents(
  match: Pick<OpenMatchItem, "totalPriceCents" | "totalPlayers" | "priceMode">,
) {
  return Math.round(
    Number(match.totalPriceCents || 0) /
      Math.max(1, Number(match.totalPlayers || 1)),
  );
}

export function sortOpenMatches(matches: OpenMatchItem[]) {
  return [...matches].sort((left, right) => {
    const leftSlots = resolveOpenMatchAvailableSlots(left);
    const rightSlots = resolveOpenMatchAvailableSlots(right);
    if (leftSlots !== rightSlots) return rightSlots - leftSlots;

    const leftStart = parseOpenMatchDateTimeValue(left.startAt)?.getTime();
    const rightStart = parseOpenMatchDateTimeValue(right.startAt)?.getTime();
    if (Number.isFinite(leftStart) && Number.isFinite(rightStart) && leftStart !== rightStart) {
      return leftStart - rightStart;
    }

    return String(left.clubName || "").localeCompare(String(right.clubName || ""), "pt-BR");
  });
}

export function resolveOpenMatchDisplayTitle(
  match: Pick<OpenMatchItem, "title" | "organizer">,
) {
  const customTitle = String(match.title || "").trim();
  if (customTitle) return customTitle;
  return buildOpenMatchDefaultTitle(match.organizer?.name);
}

export function buildOpenMatchDefaultTitle(organizerName?: string | null) {
  const normalizedName = String(organizerName || "").trim();
  return normalizedName ? `Partida by ${normalizedName}` : siteTexts.openMatches.title;
}

export function buildOpenMatchLocationQuery(
  match: Pick<OpenMatchItem, "address" | "city" | "state">,
) {
  return [match.address, match.city, match.state]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join(", ");
}

export function buildOpenMatchMapUrl(
  match: Pick<OpenMatchItem, "address" | "city" | "state">,
) {
  const query = buildOpenMatchLocationQuery(match);
  if (!query) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

export function resolveOpenMatchStartMinutes(match: Pick<OpenMatchItem, "startAt">) {
  const startAt = parseOpenMatchDateTimeValue(match.startAt);
  if (!startAt) return null;
  return startAt.getHours() * 60 + startAt.getMinutes();
}

export function formatOpenMatchScheduleRange(
  match: Pick<OpenMatchItem, "startAt" | "endAt">,
) {
  const startAt = parseOpenMatchDateTimeValue(match.startAt);
  const endAt = parseOpenMatchDateTimeValue(match.endAt);
  if (!startAt) return formatOpenMatchTimeLabel(match.endAt);
  if (!endAt) {
    return `${formatOpenMatchDateLabel(match.startAt)} • ${formatOpenMatchTimeLabel(match.startAt)}`;
  }

  const sameDay =
    startAt.getFullYear() === endAt.getFullYear() &&
    startAt.getMonth() === endAt.getMonth() &&
    startAt.getDate() === endAt.getDate();

  if (sameDay) {
    return `${formatOpenMatchDateLabel(match.startAt)} • ${formatOpenMatchTimeLabel(match.startAt)} às ${formatOpenMatchTimeLabel(match.endAt)}`;
  }

  return `${formatOpenMatchDateLabel(match.startAt)} ${formatOpenMatchTimeLabel(match.startAt)} até ${formatOpenMatchDateLabel(match.endAt)} ${formatOpenMatchTimeLabel(match.endAt)}`;
}

export function formatOpenMatchCategories(categories: string[]) {
  const uniqueCategories = Array.from(new Set((categories || []).filter(Boolean)));
  return uniqueCategories
    .map((category) => formatOpenMatchCategory(category))
    .filter(Boolean)
    .join(" • ");
}

export function formatOpenMatchCategory(category: string) {
  if (category === "7a") return siteTexts.common.category.c7a;
  if (category === "6a") return siteTexts.common.category.c6a;
  if (category === "5a") return siteTexts.common.category.c5a;
  if (category === "4a") return siteTexts.common.category.c4a;
  if (category === "3a") return siteTexts.common.category.c3a;
  if (category === "2a") return siteTexts.common.category.c2a;
  if (category === "open") return siteTexts.common.category.open;
  if (category === "free") return siteTexts.common.category.free;
  return String(category || "").trim();
}

export function resolveOpenMatchCategoryClass(category?: string | null) {
  const normalized = String(category || "").trim().toLowerCase();
  if (normalized === "open") return "open";
  if (normalized === "free") return "free";
  if (/^[2-7]a$/.test(normalized)) return normalized;
  return "default";
}

export function formatOpenMatchGenderPreference(value: OpenMatchGenderPreference) {
  const options = siteTexts.openMatches.options;
  if (value === "female") return options.genderFemale;
  if (value === "male") return options.genderMale;
  if (value === "mixed") return options.genderMixed;
  return options.genderAny;
}

export function formatOpenMatchSidePreference(value: OpenMatchSidePreference) {
  const options = siteTexts.openMatches.options;
  if (value === "left") return options.sideLeft;
  if (value === "right") return options.sideRight;
  if (value === "both") return options.sideBoth;
  return options.sideAny;
}

export function formatOpenMatchSideRequirement(
  match: Pick<
    OpenMatchItem,
    "sidePreference" | "leftPlayersCount" | "rightPlayersCount"
  >,
) {
  const leftCount = Math.max(0, Number(match.leftPlayersCount || 0));
  const rightCount = Math.max(0, Number(match.rightPlayersCount || 0));
  const detailTexts = siteTexts.openMatches.detail;
  const parts = [];

  if (leftCount > 0) {
    parts.push(`${leftCount} ${detailTexts.leftSidePlayersNeeded}`);
  }
  if (rightCount > 0) {
    parts.push(`${rightCount} ${detailTexts.rightSidePlayersNeeded}`);
  }
  if (parts.length > 0) return parts.join(" • ");
  return detailTexts.sideRequirementAny;
}

export function formatOpenMatchPaymentMode(value: OpenMatchPaymentMode) {
  const options = siteTexts.openMatches.options;
  if (value === "organizer") return options.paymentOrganizer;
  if (value === "club") return options.paymentClub;
  if (value === "onsite") return options.paymentOnsite;
  if (value === "pix") return options.paymentPix;
  if (value === "custom") return options.paymentCustom;
  if (value === "free") return options.paymentFree;
  return options.paymentUndefined;
}

export function formatOpenMatchCurrency(cents: number) {
  const value = Number(cents || 0) / 100;
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

export function formatOpenMatchPerPlayerPrice(
  match: Pick<OpenMatchItem, "totalPriceCents" | "totalPlayers" | "priceMode">,
  separator = siteTexts.openMatches.card.priceRangeSeparator,
) {
  const totalPriceCents = Number(match.totalPriceCents || 0);
  const configuredPlayers = Math.max(1, Number(match.totalPlayers || 1));
  const minPerPlayer = Math.round(totalPriceCents / configuredPlayers);
  if (match.priceMode === "player") return formatOpenMatchCurrency(minPerPlayer);
  if (configuredPlayers <= 4) return formatOpenMatchCurrency(minPerPlayer);

  const maxPerPlayer = Math.round(totalPriceCents / 4);
  if (minPerPlayer === maxPerPlayer) {
    return formatOpenMatchCurrency(minPerPlayer);
  }
  return `${formatOpenMatchCurrency(minPerPlayer)} ${separator} ${formatOpenMatchCurrency(maxPerPlayer)}`;
}

export function listUpcomingOpenMatchesForClub(
  matches: OpenMatchItem[],
  clubId: number,
  nowInput: Date | string | number = new Date(),
) {
  const now = new Date(nowInput);
  const nowTime = now.getTime();
  return matches
    .filter((match) => Number(match.clubId) === Number(clubId))
    .filter((match) => {
      const startAt = parseOpenMatchDateTimeValue(match.startAt)?.getTime();
      return Number.isFinite(startAt) && startAt >= nowTime;
    })
    .sort((left, right) => {
      const leftStart = parseOpenMatchDateTimeValue(left.startAt)?.getTime() || 0;
      const rightStart = parseOpenMatchDateTimeValue(right.startAt)?.getTime() || 0;
      return leftStart - rightStart;
    });
}
